// DS9.4.1, DS9.4.1M
import React, { useCallback, useState } from 'react';
import classnames from 'classnames';
import PromoBadge from '@wix/da-ds/pkg/Badges/PromoBadge';
import JournalTiptapViewer from '@wix/da-editor-tiptap/pkg/components/Viewer/presets/Journal';
import { MeasuredCookieType } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';
import PromoBadgeWithTooltip from '../../../PromoBadgeWithTooltip';
import { useSubscriptionTier } from '../../../utils/hooks/useSubscriptionTier';
import TextRenderer from '../../../TextRenderer';
import { useDeviationCoverImage } from '../../hooks/useDeviationCoverImage';
import ReadingTime from '../_partials/ReadingTime';
import FeedCardLayout from '../_partials/FeedCardLayout';
import FeedCardTitle from '../_partials/FeedCardTitle';
import { FeedCardProps } from '../types';
import {
  DEFAULT_COVER_IMAGE_RESOLUTION_WIDTH,
  DEFAULT_COVER_IMAGE_RESOLUTION_HEIGHT,
} from '../constants';

import legacyTheme from '../feedcard.legacy.theme.scss';
import s from './Journal.scss';

const JournalFeedCardContents: React.FC<FeedCardProps> = ({
  className,
  deviation,
  extraActions,
  withWatchOrSubscribeButton,
}) => {
  const { tier, isUserSubscribed, needsSubscription, isSubscribedButBlocked } =
    useSubscriptionTier(deviation);

  // maintain this so we can track when we can enable scroll restoration on duperbrowse
  const [isLayoutReady, setIsLayoutReady] = useState(false);
  const onLayoutReady = useCallback(() => {
    setIsLayoutReady(true);
  }, []);

  const { asBackgroundStyleProp } = useDeviationCoverImage({
    deviation,
    width: DEFAULT_COVER_IMAGE_RESOLUTION_WIDTH,
    height: DEFAULT_COVER_IMAGE_RESOLUTION_HEIGHT,
    allowHuge: true,
  });

  return (
    <FeedCardLayout
      className={classnames(s['root'], className)}
      deviation={deviation}
      extraActions={extraActions}
      isExpandable
      // we need to force the toggle button to be shown when unsubscribed/blocked
      // because the BE truncates text and the content is never tall enough to
      // cause the toggle to be shown using the normal means
      forceShowExpansionToggle={needsSubscription || isSubscribedButBlocked}
      withCommentControl
      withFaveControl
      withWatchOrSubscribeButton={withWatchOrSubscribeButton}
      onLayoutReady={onLayoutReady}
    >
      {asBackgroundStyleProp && (
        <div className={s['cover-image']} style={asBackgroundStyleProp}>
          <div className={s['fade']} />
        </div>
      )}

      <div className={s['title-area']}>
        {!!tier &&
          (isUserSubscribed ? (
            <PromoBadge
              badgeType="tier"
              size={40}
              className={s['tier-badge']}
            />
          ) : (
            <PromoBadgeWithTooltip
              badgeType="tier"
              size={40}
              className={s['tier-badge']}
              isLocked={!isUserSubscribed}
            />
          ))}

        <FeedCardTitle
          className={s['title']}
          deviation={deviation}
          disableScrollRestoration={!isLayoutReady}
        />

        <div className={s['reading-time']}>
          <ReadingTime deviation={deviation} />
        </div>
      </div>

      <TextRenderer
        textContent={deviation.textContent}
        viewerKey={deviation.deviationId}
        legacyTheme="journal"
        className={classnames(
          s['content'],
          needsSubscription && s['show-at-least-two-lines'],
          legacyTheme['legacy'],
          legacyTheme['override'],
          s['feed-width']
        )}
        tipTapViewer={JournalTiptapViewer}
        cookieType={MeasuredCookieType.BROWSE_POST_FEED}
        defaultSize={{
          desktop: { width: 600, height: 600 },
          mobile: { width: 380, height: 400 },
        }}
      />
    </FeedCardLayout>
  );
};
JournalFeedCardContents.displayName = 'JournalFeedCardContents';
export default JournalFeedCardContents;
